import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Invoice, { ManualTipInput, Tip as InvoiceTip } from '@spike/invoice-model';
import { InvoicesStatus, saveTipThunk } from '@spike/invoices-action';
import { Option } from '@spike/model';
import { PaymentMethodIds } from '@spike/payments-model';
import useNonInitialEffect from '@versiondos/hooks';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { useApiClientWrapper, usePermissions } from 'hooks';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isTemporalEmail } from 'utils/ValidationUtils';
import PaymentMethods from './PaymentMethods';
import Section from './Section';
import SendingInformation from './SendingInformation';
import Tips from './Tips';
import { isSameTip } from './Tips/TipUtils';

export interface CheckoutPaymentProps {
    invoice: Invoice;
    isBooking: boolean;
    paymentsEnabled: boolean;
    paymentMethods?: Array<PaymentMethodIds>;
    hideTips?: boolean;
    disableTips?: boolean;
    hideSendingInformation?: boolean;
    disabled?: boolean;
    initPaymentMethodId?: string | null;
    className?: string;
    paymentMethodError?: boolean;
    walletBalance?: number;
    onSavingTip: (saving: boolean) => void;
    onChangePaymentInfo: (paymentInfo: PaymentInfo) => void;
    onTipDistributionValidChange?: (valid: boolean) => void;
}

export interface PaymentInfo {
    paymentMethodId: string | null;
    walletApplied: boolean;
    walletAmount: number;
    tip: InvoiceTip | null;
    email: string | null;
    sendInvoice: boolean;
    posTerminalId: number | null;
    posServiceId: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        section: {
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        alert: {
            marginBottom: '0px !important',
            height: 53
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        },
        walletInfo: {
            marginTop: 16,
            padding: '12px 16px',
            background: 'var(--Brown-Light, #F8F5F1)',
            borderRadius: 16,
            border: '2px solid var(--Nude-Dark, #BCB8AE)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        walletInfoLabel: {
            fontSize: 14,
            color: '#222222',
            fontWeight: 500
        },
        walletInfoAmount: {
            fontSize: 14,
            color: '#222222',
            fontWeight: 600
        },
        walletZeroLabel: {
            fontSize: 14,
            color: '#7A7A7A'
        }
    })
);

export const CheckoutPayment: FunctionComponent<CheckoutPaymentProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const hasPremiumPlan = useHasPremiumPlan();

    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const baseStaffIds = useMemo<ReadonlySet<number>>(
        () => new Set(props.invoice.lines.filter(l => !l.isProduct && l.staff?.id !== undefined).map(l => l.staff!.id)),
        [props.invoice.lines]
    );

    const walletBalance = props.walletBalance ?? 0;

    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
        paymentMethodId: props.initPaymentMethodId ?? null,
        walletApplied: false,
        walletAmount: 0,
        tip: props.invoice.tip,
        email: isTemporalEmail(props.invoice?.customer?.email ?? '') ? '' : props.invoice?.customer?.email ?? null,
        sendInvoice:
            !isTemporalEmail(props.invoice?.customer?.email ?? '') &&
            (props.invoice?.customer?.email ?? '').trim().length > 0,
        posTerminalId: null,
        posServiceId: null
    });

    const [show, setShow] = useState({
        tips: props.initPaymentMethodId !== PaymentMethodIds.POS,
        tipsAlert: props.initPaymentMethodId === PaymentMethodIds.POS
    });

    const [prevTip, setPrevTip] = useState<InvoiceTip | null>(props.invoice.tip ? { ...props.invoice.tip } : null);

    const [saving, setSaving] = useState(false);

    const walletSelected = paymentInfo.paymentMethodId === PaymentMethodIds.Wallet;

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.SaveTipSuccess:
            case InvoicesStatus.Error:
                props.onSavingTip(false);
                setSaving(false);
                break;
        }
    }, [invoicesStatus]);

    useEffect(() => props.onChangePaymentInfo(paymentInfo), [paymentInfo]);

    useNonInitialEffect(() => {
        if (!isSameTip(paymentInfo.tip, props.invoice.tip)) {
            setPaymentInfo(prev => ({ ...prev, tip: props.invoice.tip }));
            setPrevTip(props.invoice.tip ? { ...props.invoice.tip } : null);
        }
    }, [props.invoice.tip?.amount, props.invoice.tip?.percentage]);

    useNonInitialEffect(() => {
        if (Number(props.invoice.balance) <= 0) {
            changePaymentMethodHandler(null);
        }
    }, [props.invoice.balance]);

    const saveTip = (amount: string | null, percentage: string | null, tipsToDestroy?: Array<ManualTipInput>) => {
        props.onSavingTip(true);
        setSaving(true);
        dispatch(saveTipThunk(apiClientWrapper, props.invoice.id!, amount, percentage, tipsToDestroy));
    };

    const applyPaymentMethodTipState = (methodId: string | null, prevMethodId: string | null) => {
        if (methodId === PaymentMethodIds.POS) {
            Number(props.invoice.tip?.amount) !== Number(props.invoice.balance) && saveTip(null, null);
        } else if (prevMethodId === PaymentMethodIds.POS) {
            saveTip(prevTip?.amount || null, prevTip?.percentage || null);
        }

        return {
            tips: methodId !== PaymentMethodIds.POS,
            tipsAlert: methodId === PaymentMethodIds.POS
        };
    };

    const changePaymentMethodHandler = (paymentMethod: Option<string> | null) => {
        if (paymentMethod === null) {
            setPaymentInfo(prev => ({
                ...prev,
                paymentMethodId: null,
                walletApplied: false,
                walletAmount: 0
            }));
            setShow({ tips: true, tipsAlert: false });
            return;
        }

        if (paymentMethod.id === PaymentMethodIds.Wallet) {
            const prevMethodId = paymentInfo.paymentMethodId;
            if (prevMethodId === PaymentMethodIds.POS) {
                saveTip(prevTip?.amount || null, prevTip?.percentage || null);
            }
            setPaymentInfo(prev => ({
                ...prev,
                paymentMethodId: PaymentMethodIds.Wallet,
                walletApplied: true,
                walletAmount: 0,
                tip: prevMethodId === PaymentMethodIds.POS ? prevTip : prev.tip
            }));
            setShow({ tips: true, tipsAlert: false });
            return;
        }

        const prevMethodId = paymentInfo.paymentMethodId;
        const newShow = applyPaymentMethodTipState(paymentMethod.id, prevMethodId);

        setPaymentInfo(prev => ({
            ...prev,
            paymentMethodId: paymentMethod.id,
            walletApplied: false,
            walletAmount: 0,
            tip:
                paymentMethod.id === PaymentMethodIds.POS
                    ? null
                    : prevMethodId === PaymentMethodIds.POS
                    ? prevTip
                    : prev.tip
        }));
        setShow(newShow);
    };

    const changeTipHandler = (tip: InvoiceTip | null, tipsToDestroy?: Array<ManualTipInput>, force?: boolean) => {
        const hasTipsToDestroy = (tipsToDestroy?.length ?? 0) > 0;
        if (force || hasTipsToDestroy || !isSameTip(paymentInfo.tip, tip)) {
            setPaymentInfo(prev => ({ ...prev, tip }));
            setPrevTip(tip);
            saveTip(tip === null ? null : tip.amount, tip === null ? null : tip.percentage, tipsToDestroy);
        }
    };

    const changeSendInvoiceHandler = (sendInvoice: boolean, email: string, isEmailValid: boolean) => {
        setPaymentInfo(prev => ({ ...prev, sendInvoice, email: isEmailValid ? email : null }));
    };

    return (
        <>
            <PaymentMethods
                hideTitle={true}
                paymentsEnabled={props.paymentsEnabled}
                paymentMethods={props.paymentMethods}
                selectedId={paymentInfo.paymentMethodId}
                hasPremiumPlan={hasPremiumPlan}
                disabled={props.disabled || Number(props.invoice.balance) <= 0}
                onSelect={changePaymentMethodHandler}
                source="booking"
                error={props.paymentMethodError}
                walletBalance={walletBalance}
            />

            {walletSelected && (
                <Box className={classes.walletInfo}>
                    {walletBalance > 0 ? (
                        <>
                            <Typography className={classes.walletInfoLabel}>Wallet Balance</Typography>
                            <Typography className={classes.walletInfoAmount}>${walletBalance.toFixed(2)}</Typography>
                        </>
                    ) : (
                        <Typography className={classes.walletZeroLabel}>No wallet balance available.</Typography>
                    )}
                </Box>
            )}

            {show.tips && !props.hideTips && (
                <Tips
                    invoiceId={props.invoice.id!}
                    invoiceTip={paymentInfo.tip}
                    subtotalWithoutTipTaxes={props.invoice.subtotal}
                    tipDistribution={props.invoice.tipDistribution ?? []}
                    baseStaffIds={baseStaffIds}
                    saving={saving}
                    tipsManual={props.invoice.tipsManual}
                    disabled={props.disabled || props.disableTips}
                    className={classes.section}
                    onChange={changeTipHandler}
                    onTipDistributionValidChange={props.onTipDistributionValidChange}
                />
            )}
            {show.tipsAlert && (
                <Section
                    title="Add a tip"
                    className={classes.section}
                >
                    <AlertNewBooking
                        className={classes.alert}
                        bWidth="2px"
                        icon={faExclamationCircle}
                        iconSize="lg"
                        iconColor="#BAA997"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <Typography className={classes.alertText}>
                            You can select the tip directly in the terminal.
                        </Typography>
                    </AlertNewBooking>
                </Section>
            )}
            {!props.hideSendingInformation && (
                <SendingInformation
                    className={classes.section}
                    email={props.invoice.customer?.email ?? ''}
                    disabled={props.disabled}
                    onChange={changeSendInvoiceHandler}
                    hideEmail={!permissions.clients.viewSensitiveContact}
                />
            )}
        </>
    );
};

export default CheckoutPayment;
